/**
 * The one place that decides what a report's description says.
 *
 * There used to be two: this sentence was built inline in ReportPage.jsx with a
 * template literal, and again in netlify/edge-functions/report-preview.js with a
 * filtered join. The edge function's own comment claimed the two "cannot
 * disagree" — they did. `one_line_verdict` defaults to "" in the backend, so a
 * report without a verdict unfurled as "Guardian · Lean Left" to a crawler and
 * rendered as "Guardian · Lean Left · " to Google, which does execute the page.
 *
 * The edge function still carries its own copy, because it runs on Deno at the
 * CDN and does not share this bundle. That copy is held to this one by
 * src/__tests__/meta-contract.test.js, which runs both over the same table and
 * asserts byte-identical output.
 */

/** Length beyond which Google reliably truncates a description in results. */
export const DESCRIPTION_LIMIT = 160;

/**
 * Trimmed rather than left long: an over-length description is not penalised,
 * it is just cut mid-sentence in the result, which reads worse than a shorter
 * one that finishes its thought.
 */
export function trimDescription(text) {
  const s = String(text ?? "");
  if (s.length <= DESCRIPTION_LIMIT) return s;
  let cut = s.slice(0, DESCRIPTION_LIMIT - 1);
  // slice() counts UTF-16 code units, so cutting at a fixed index can land
  // between the halves of a surrogate pair -- an emoji, CJK Ext-B, a lot of
  // transliterated names. The lone half becomes U+FFFD the moment the response
  // is serialised to UTF-8, and ships that way to every crawler and to Google.
  const lastUnit = cut.charCodeAt(cut.length - 1);
  if (lastUnit >= 0xd800 && lastUnit <= 0xdbff) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

/**
 * Outlet, lean and verdict as one sentence, dropping whichever of the three the
 * report does not have. Trimmed to DESCRIPTION_LIMIT.
 */
export function composeDescription(outlet, leanLabel, verdict) {
  return trimDescription([outlet, leanLabel, verdict].filter(Boolean).join(" · "));
}
